import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Plus, ShieldCheck, Trash2, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import SuccessToast from "../components/SuccessToast";
import SeatLayoutBuilder from "./SeatLayoutBuilder";
import {
  DEFAULT_START_COLUMN_NUMBER,
  DEFAULT_START_ROW_LABEL,
  findDuplicateSeatLabels,
  formatDuplicateSeatMessage,
  generateSeats,
  normalizeNumberDirection,
  normalizeRowLabel,
  parseColumnStart,
  renumberSeats,
  resizeSeats,
  sectionOrigin,
  validateGridSettings,
  wouldAddDuplicateSeatLabel,
  wouldResizeDeleteSeats,
} from "../lib/venueLayout";
import { isMissingPolicyColumn, isMissingVenueTable } from "../lib/venuesApi";
import { isMissingColorColumn, normalizeHex } from "../lib/sectionColor";
import { normalizeVenuePolicyHtml } from "../lib/venuePolicyHtml";
import {
  idsToDelete,
  parentCheckState,
  pruneSelection,
  toggleSectionSelection,
  toggleSelectAll,
} from "../lib/venueSectionSelection";
import { adminDangerBtn } from "./adminUi";
import VenuePolicyEditor from "./VenuePolicyEditor";

const isMissingStartPositionColumn = (error) => {
  if (!error) return false;
  const text = `${error.code || ""} ${error.message || ""} ${error.details || ""}`.toLowerCase();
  return (
    text.includes("42703") ||
    text.includes("start_row_label") ||
    text.includes("start_column_number")
  );
};

const isMissingNumberDirectionColumn = (error) => {
  if (!error) return false;
  const text = `${error.code || ""} ${error.message || ""} ${error.details || ""}`.toLowerCase();
  return text.includes("number_direction");
};

const createLocalId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `section-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const emptyVenue = {
  name: "",
  address: "",
  seat_plan_image_url: "",
  policy_html: "",
};

const VenueEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [venue, setVenue] = useState(emptyVenue);
  const [sections, setSections] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingDeleteIds, setPendingDeleteIds] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [duplicateSeatError, setDuplicateSeatError] = useState("");
  const [referencedEventCount, setReferencedEventCount] = useState(0);

  const showError = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => setErrorToastMessage(""), 3500);
  };

  const showDuplicateSeatError = (labels) => {
    setDuplicateSeatError(formatDuplicateSeatMessage(labels));
  };

  const loadVenue = async () => {
    if (isNew) return;
    setLoading(true);

    const [{ data: venueRow, error: venueError }, { data: sectionRows, error: sectionError }] = await Promise.all([
      supabase.from("venues").select("*").eq("id", id).maybeSingle(),
      supabase.from("venue_sections").select("*").eq("venue_id", id).order("sort_order", { ascending: true }),
    ]);

    if (venueError || sectionError) {
      if (isMissingVenueTable(venueError || sectionError)) {
        showError("Run create_venue_management.sql before editing venues.");
      } else {
        showError("Failed to load venue.");
      }
      setLoading(false);
      return;
    }

    if (!venueRow) {
      showError("Venue not found.");
      setLoading(false);
      return;
    }

    const sectionList = sectionRows || [];
    const sectionIds = sectionList.map((section) => section.id);
    let seats = [];
    if (sectionIds.length) {
      const { data: seatRows, error: seatError } = await supabase
        .from("venue_seats")
        .select("section_id, row_position, column_position, row_label, column_number, seat_label")
        .in("section_id", sectionIds);
      if (seatError) {
        showError("Failed to load seats.");
        setLoading(false);
        return;
      }
      seats = seatRows || [];
    }

    const seatsBySection = {};
    seats.forEach((seat) => {
      if (!seatsBySection[seat.section_id]) seatsBySection[seat.section_id] = [];
      seatsBySection[seat.section_id].push(seat);
    });

    setVenue({
      name: venueRow.name || "",
      address: venueRow.address || "",
      seat_plan_image_url: venueRow.seat_plan_image_url || "",
      policy_html: normalizeVenuePolicyHtml(venueRow.policy_html),
      is_archived: venueRow.is_archived,
    });
    setImagePreview(venueRow.seat_plan_image_url || "");
    const nextSections = sectionList.map((section) => ({
      ...section,
      seats: seatsBySection[section.id] || [],
    }));
    setSections(nextSections);
    setActiveSectionId(nextSections[0]?.id || "");

    try {
      const { count } = await supabase
        .from("events")
        .select("id", { count: "exact", head: true })
        .eq("venue_id", id);
      setReferencedEventCount(count || 0);
    } catch (error) {
      setReferencedEventCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const topLevelSections = useMemo(
    () => sections.filter((section) => !section.parent_section_id),
    [sections]
  );

  const childrenOf = (parentId) =>
    sections
      .filter((section) => section.parent_section_id === parentId)
      .slice()
      .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0));

  const activeSection = sections.find((section) => section.id === activeSectionId) || null;
  const activeHasChildren = activeSection ? childrenOf(activeSection.id).length > 0 : false;

  const updateSection = (sectionId, patch) => {
    setSections((prev) =>
      prev.map((section) => (section.id === sectionId ? { ...section, ...patch } : section))
    );
  };

  const applySeatsChange = (sectionId, nextSeats) => {
    const section = sections.find((row) => row.id === sectionId);
    const currentSeats = section?.seats || [];
    const conflicts = wouldAddDuplicateSeatLabel(currentSeats, nextSeats);
    if (conflicts.length) {
      showDuplicateSeatError(conflicts);
      return;
    }
    updateSection(sectionId, { seats: nextSeats });
  };

  const addTopLevelSection = () => {
    const next = {
      id: createLocalId(),
      name: `Section ${topLevelSections.length + 1}`,
      parent_section_id: null,
      sort_order: topLevelSections.length,
      row_count: 0,
      column_count: 0,
      start_row_label: DEFAULT_START_ROW_LABEL,
      start_column_number: DEFAULT_START_COLUMN_NUMBER,
      number_direction: "ltr",
      color: "",
      seats: [],
    };
    setSections((prev) => [...prev, next]);
    setActiveSectionId(next.id);
  };

  const addSubsection = (parentId) => {
    const parent = sections.find((section) => section.id === parentId);
    if (!parent) return;
    if ((parent.seats || []).length) {
      const confirmed = window.confirm(
        "Adding a subsection turns this section into a parent. Its current seats will be cleared. Continue?"
      );
      if (!confirmed) return;
    }
    const kids = childrenOf(parentId);
    const next = {
      id: createLocalId(),
      name: `Subsection ${kids.length + 1}`,
      parent_section_id: parentId,
      sort_order: kids.length,
      row_count: 0,
      column_count: 0,
      start_row_label: DEFAULT_START_ROW_LABEL,
      start_column_number: DEFAULT_START_COLUMN_NUMBER,
      number_direction: "ltr",
      color: "",
      seats: [],
    };
    setSections((prev) =>
      prev
        .map((section) =>
          section.id === parentId
            ? { ...section, row_count: 0, column_count: 0, seats: [] }
            : section
        )
        .concat(next)
    );
    setActiveSectionId(next.id);
  };

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = pruneSelection(sections, prev);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [sections]);

  const allSectionsSelected = sections.length > 0 && sections.every((section) => selectedIds.has(section.id));
  const someSectionsSelected = sections.some((section) => selectedIds.has(section.id));

  const openBulkDelete = () => {
    const removeIds = idsToDelete(sections, selectedIds);
    if (!removeIds.size) return;
    setPendingDeleteIds(removeIds);
  };

  const applyBulkDelete = () => {
    if (!pendingDeleteIds?.size) return;
    setSections((prev) => prev.filter((section) => !pendingDeleteIds.has(section.id)));
    if (pendingDeleteIds.has(activeSectionId)) setActiveSectionId("");
    setSelectedIds(new Set());
    setPendingDeleteIds(null);
  };

  useEffect(() => {
    if (!pendingDeleteIds) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPendingDeleteIds(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [pendingDeleteIds]);

  const pendingDeleteRows = pendingDeleteIds
    ? sections
        .filter((section) => pendingDeleteIds.has(section.id))
        .map((section) => {
          const parent = sections.find((row) => row.id === section.parent_section_id);
          const name = section.name?.trim() || "Untitled section";
          return {
            id: section.id,
            label: parent ? `${parent.name?.trim() || "Section"} — ${name}` : name,
          };
        })
    : [];

  const handleGenerateGrid = () => {
    if (!activeSection || activeHasChildren) return;
    const nextRows = Math.max(0, parseInt(activeSection.row_count, 10) || 0);
    const nextCols = Math.max(0, parseInt(activeSection.column_count, 10) || 0);
    const validationError = validateGridSettings({
      rowCount: activeSection.row_count,
      columnCount: activeSection.column_count,
      startRowLabel: activeSection.start_row_label ?? DEFAULT_START_ROW_LABEL,
      startColumnNumber: activeSection.start_column_number ?? DEFAULT_START_COLUMN_NUMBER,
    });
    if (validationError) {
      showError(validationError);
      return;
    }

    const origin = sectionOrigin(activeSection);
    const currentSeats = activeSection.seats || [];
    if (!currentSeats.length) {
      updateSection(activeSection.id, {
        row_count: nextRows,
        column_count: nextCols,
        seats: generateSeats(nextRows, nextCols, origin),
      });
      return;
    }

    if (wouldResizeDeleteSeats(currentSeats, nextRows, nextCols)) {
      const confirmed = window.confirm(
        "This grid size will drop seats that no longer fit. Seats that remain stay in place, and their seat numbers are recalculated. Continue?"
      );
      if (!confirmed) return;
    }

    updateSection(activeSection.id, {
      row_count: nextRows,
      column_count: nextCols,
      seats: resizeSeats(currentSeats, nextRows, nextCols, origin),
    });
  };

  const uploadSeatPlanImage = async (venueId, file) => {
    const fileName = `venues/${venueId}/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("event-images").upload(fileName, file);
    if (error) throw error;
    const { data } = supabase.storage.from("event-images").getPublicUrl(fileName);
    return data.publicUrl;
  };

  const validateBeforeSave = () => {
    if (!venue.name.trim()) {
      showError("Venue name is required.");
      return false;
    }

    const leafSectionsForValidation = sections.filter(
      (section) => childrenOf(section.id).length === 0
    );
    for (const section of leafSectionsForValidation) {
      const duplicates = findDuplicateSeatLabels(section.seats || []);
      if (duplicates.length) {
        const sectionName = section.name?.trim() || "Untitled section";
        setDuplicateSeatError(
          `${formatDuplicateSeatMessage(duplicates)} (${sectionName})`
        );
        return false;
      }
    }

    return true;
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (saving) return;
    if (!validateBeforeSave()) return;
    setShowSaveConfirm(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!validateBeforeSave()) {
      setShowSaveConfirm(false);
      return;
    }

    setShowSaveConfirm(false);
    setSaving(true);
    try {
      let venueId = isNew ? null : id;
      const policyHtml = normalizeVenuePolicyHtml(venue.policy_html) || null;
      const venuePayload = {
        name: venue.name.trim(),
        address: venue.address.trim() || null,
        seat_plan_image_url: venue.seat_plan_image_url || null,
        policy_html: policyHtml,
      };
      const venuePayloadWithoutPolicy = { ...venuePayload };
      delete venuePayloadWithoutPolicy.policy_html;
      let policyUnsupported = false;

      const persistVenueRow = async (payload) => {
        if (isNew && !venueId) {
          const { data: inserted, error } = await supabase
            .from("venues")
            .insert(payload)
            .select("id")
            .single();
          return { inserted, error };
        }
        const { error } = await supabase.from("venues").update(payload).eq("id", venueId);
        return { inserted: null, error };
      };

      let persistResult = await persistVenueRow(venuePayload);
      if (persistResult.error && isMissingPolicyColumn(persistResult.error)) {
        persistResult = await persistVenueRow(venuePayloadWithoutPolicy);
        policyUnsupported = !persistResult.error;
      }
      if (persistResult.error) throw persistResult.error;
      if (persistResult.inserted?.id) {
        venueId = persistResult.inserted.id;
      }

      if (imageFile) {
        const publicUrl = await uploadSeatPlanImage(venueId, imageFile);
        const { error: imageError } = await supabase
          .from("venues")
          .update({ seat_plan_image_url: publicUrl })
          .eq("id", venueId);
        if (imageError) throw imageError;
        setVenue((prev) => ({ ...prev, seat_plan_image_url: publicUrl }));
        setImagePreview(publicUrl);
        setImageFile(null);
      }

      const { data: existingSections, error: existingSectionError } = await supabase
        .from("venue_sections")
        .select("id")
        .eq("venue_id", venueId);
      if (existingSectionError) throw existingSectionError;

      const localIds = new Set(sections.map((section) => section.id));
      const removedIds = (existingSections || [])
        .map((row) => row.id)
        .filter((sectionId) => !localIds.has(sectionId));
      if (removedIds.length) {
        const { error: deleteError } = await supabase.from("venue_sections").delete().in("id", removedIds);
        if (deleteError) throw deleteError;
      }

      const parents = sections.filter((section) => !section.parent_section_id);
      const children = sections.filter((section) => section.parent_section_id);
      let startPositionUnsupported = false;
      let colorUnsupported = false;
      let numberDirectionUnsupported = false;
      const persistSection = async (section) => {
        const payload = {
          id: section.id,
          venue_id: venueId,
          parent_section_id: section.parent_section_id,
          name: section.name.trim() || "Untitled section",
          sort_order: section.sort_order || 0,
          row_count: Number(section.row_count) || 0,
          column_count: Number(section.column_count) || 0,
          start_row_label: normalizeRowLabel(section.start_row_label) || DEFAULT_START_ROW_LABEL,
          start_column_number:
            parseColumnStart(section.start_column_number) ?? DEFAULT_START_COLUMN_NUMBER,
          number_direction: normalizeNumberDirection(section.number_direction),
          color: normalizeHex(section.color) || null,
        };
        let { error } = await supabase.from("venue_sections").upsert(payload, { onConflict: "id" });
        if (!error) return;

        let nextPayload = payload;
        if (isMissingNumberDirectionColumn(error)) {
          const { number_direction: _direction, ...withoutDirection } = nextPayload;
          nextPayload = withoutDirection;
          numberDirectionUnsupported = true;
          const directionResult = await supabase.from("venue_sections").upsert(nextPayload, { onConflict: "id" });
          error = directionResult.error;
          if (!error) return;
        }

        if (isMissingColorColumn(error)) {
          const { color: _color, ...withoutColor } = nextPayload;
          nextPayload = withoutColor;
          colorUnsupported = true;
          const colorResult = await supabase.from("venue_sections").upsert(nextPayload, { onConflict: "id" });
          error = colorResult.error;
          if (!error) return;
        }

        if (!isMissingStartPositionColumn(error)) throw error;

        const { start_row_label: _row, start_column_number: _column, ...legacyPayload } = nextPayload;
        startPositionUnsupported = true;
        const { error: legacyError } = await supabase
          .from("venue_sections")
          .upsert(legacyPayload, { onConflict: "id" });
        if (!legacyError) return;
        if (isMissingNumberDirectionColumn(legacyError)) {
          const { number_direction: _direction, ...withoutDirection } = legacyPayload;
          numberDirectionUnsupported = true;
          const directionResult = await supabase
            .from("venue_sections")
            .upsert(withoutDirection, { onConflict: "id" });
          if (directionResult.error) throw directionResult.error;
          return;
        }
        if (isMissingColorColumn(legacyError)) {
          const { color: _legacyColor, ...legacyWithoutColor } = legacyPayload;
          colorUnsupported = true;
          const lastResult = await supabase
            .from("venue_sections")
            .upsert(legacyWithoutColor, { onConflict: "id" });
          if (lastResult.error) throw lastResult.error;
          return;
        }
        throw legacyError;
      };
      for (const section of parents) await persistSection(section);
      for (const section of children) await persistSection(section);

      const leafSections = sections.filter((section) => childrenOf(section.id).length === 0);
      const leafIds = leafSections.map((section) => section.id);
      if (leafIds.length) {
        const { error: clearSeatsError } = await supabase.from("venue_seats").delete().in("section_id", leafIds);
        if (clearSeatsError) throw clearSeatsError;
      }

      const seatRows = leafSections.flatMap((section) =>
        renumberSeats(section.seats || [], sectionOrigin(section)).map((seat) => ({
          section_id: section.id,
          row_position: seat.row_position,
          column_position: seat.column_position,
          row_label: seat.row_label,
          column_number: seat.column_number,
          seat_label: seat.seat_label,
        }))
      );
      if (seatRows.length) {
        const { error: insertSeatsError } = await supabase.from("venue_seats").insert(seatRows);
        if (insertSeatsError) throw insertSeatsError;
      }

      if (startPositionUnsupported) {
        showError(
          "Saved, but starting row/column was not stored. Run add_start_position_to_venue_sections.sql."
        );
      } else if (numberDirectionUnsupported) {
        showError(
          "Saved, but seat number direction was not stored. Run add_number_direction_to_venue_sections.sql."
        );
      } else if (colorUnsupported) {
        showError("Saved, but section color was not stored. Run add_color_to_venue_sections_and_ticket_tiers.sql.");
      } else if (policyUnsupported) {
        showError("Saved, but venue policy was not stored. Run add_venue_policy.sql.");
      }

      setToastMessage("Venue saved. Existing events keep their own seat and policy snapshots.");
      if (isNew) {
        window.setTimeout(() => navigate(`/admin/venues/${venueId}`), 600);
      }
    } catch (error) {
      console.error("Failed to save venue:", error);
      showError(error.message || "Failed to save venue.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f2f1]">
        <AdminTopBar />
        <div className="p-4 sm:p-6 md:p-10">
          <p className="text-sm font-bold uppercase tracking-wide text-gray-500">Loading venue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f2f1]">
      <AdminTopBar />
      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-6xl mx-auto min-w-0">
          <SuccessToast message={toastMessage} />
          <SuccessToast message={errorToastMessage} variant="error" />

          {duplicateSeatError ? (
            <div className="fixed inset-0 z-50 bg-black/50 p-4 flex items-center justify-center">
              <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl p-6">
                <h2 className="text-lg font-black text-gray-900">Duplicate seats</h2>
                <p className="mt-3 text-sm text-gray-600">{duplicateSeatError}</p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDuplicateSeatError("")}
                    className="rounded-lg bg-brand-maroon px-5 py-2 text-xs font-bold uppercase text-white hover:bg-red-700"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => navigate("/admin/venues")}
            className="flex items-center gap-2 text-gray-600 hover:text-black mb-6 font-bold text-sm uppercase"
          >
            <ChevronLeft size={20} /> {isNew ? "Create Venue" : "Edit Venue"}
          </button>

          {referencedEventCount > 0 ? (
            <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {referencedEventCount} event{referencedEventCount === 1 ? "" : "s"} use this venue.
              Published events keep their saved seat layout and policy snapshot. This editor only changes the reusable template.
            </p>
          ) : null}

          <form onSubmit={handleFormSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">Venue Name</label>
                <input
                  value={venue.name}
                  onChange={(event) => setVenue((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full p-4 rounded-xl border bg-white text-gray-900"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">Address</label>
                <input
                  value={venue.address}
                  onChange={(event) => setVenue((prev) => ({ ...prev, address: event.target.value }))}
                  className="w-full p-4 rounded-xl border bg-white text-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
                  Seat Plan Image
                </label>
                <label className="w-full aspect-video border-2 border-dashed rounded-2xl bg-white flex flex-col items-center justify-center cursor-pointer overflow-hidden">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Seat plan" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Upload size={24} className="text-gray-400 mb-2" />
                      <p className="text-xs text-gray-400 uppercase">Upload seat plan image</p>
                    </>
                  )}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      setImageFile(file);
                      setImagePreview(URL.createObjectURL(file));
                    }}
                  />
                </label>
                <p className="mt-2 text-[11px] text-gray-500">
                  Visual reference only. Seats are defined on the grid, not detected from the image.
                </p>
              </div>
            </div>

            <div className="space-y-4 min-w-0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="inline-flex items-center gap-2 font-black uppercase text-gray-800">
                  <input
                    type="checkbox"
                    aria-label="Select all sections"
                    checked={allSectionsSelected}
                    disabled={sections.length === 0}
                    ref={(element) => {
                      if (element) element.indeterminate = someSectionsSelected && !allSectionsSelected;
                    }}
                    onChange={() => setSelectedIds((prev) => toggleSelectAll(sections, prev))}
                  />
                  Sections
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={openBulkDelete}
                    disabled={!someSectionsSelected}
                    className={`${adminDangerBtn} gap-2 uppercase disabled:cursor-not-allowed disabled:opacity-50 w-full sm:w-auto`}
                  >
                    <Trash2 size={14} />
                    {someSectionsSelected ? `Delete (${selectedIds.size})` : "Delete"}
                  </button>
                  <button
                    type="button"
                    onClick={addTopLevelSection}
                    className="bg-brand-maroon text-white px-4 py-2 rounded-lg text-xs font-bold uppercase flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <Plus size={14} /> Add Section
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {topLevelSections.map((section) => (
                  <div key={section.id} className="rounded-xl border border-gray-200 bg-white">
                    <div className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Select ${section.name || "section"}`}
                        checked={parentCheckState(sections, selectedIds, section.id) === "checked"}
                        ref={(element) => {
                          if (element) {
                            element.indeterminate = parentCheckState(sections, selectedIds, section.id) === "indeterminate";
                          }
                        }}
                        onChange={() => setSelectedIds((prev) => toggleSectionSelection(sections, prev, section.id))}
                      />
                      <span
                        className="h-3 w-3 rounded-sm border border-gray-300 shrink-0"
                        style={{ backgroundColor: normalizeHex(section.color) || "#e5e7eb" }}
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        onClick={() => setActiveSectionId(section.id)}
                        className={`flex-1 text-left text-sm font-bold ${
                          activeSectionId === section.id ? "text-brand-maroon" : "text-gray-800"
                        }`}
                      >
                        {section.name}
                      </button>
                      <button type="button" onClick={() => addSubsection(section.id)} className="text-[10px] font-bold uppercase text-gray-500">
                        + Sub
                      </button>
                    </div>
                    {childrenOf(section.id).map((child) => (
                      <div key={child.id} className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 pl-8">
                        <input
                          type="checkbox"
                          aria-label={`Select ${child.name || "subsection"}`}
                          checked={selectedIds.has(child.id)}
                          onChange={() => setSelectedIds((prev) => toggleSectionSelection(sections, prev, child.id))}
                        />
                        <button
                          type="button"
                          onClick={() => setActiveSectionId(child.id)}
                          className={`flex-1 text-left text-sm ${
                            activeSectionId === child.id ? "font-bold text-brand-maroon" : "text-gray-700"
                          }`}
                        >
                          {child.name}
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 space-y-3 min-w-0">
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} className="text-brand-maroon mt-0.5 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Venue Policy &amp; Guidelines
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Add the rules and guidelines that guests should know before attending an event at this venue.
                  </p>
                </div>
              </div>
              <VenuePolicyEditor
                value={venue.policy_html}
                contentKey={isNew ? "new" : id}
                onChange={(nextHtml) => setVenue((prev) => ({ ...prev, policy_html: nextHtml }))}
              />
              <p className="text-[11px] text-gray-500">
                Formatting is saved with this venue. New events copy this policy when they are created.
              </p>
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 space-y-4 min-w-0">
              {activeSection ? (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
                      {activeHasChildren ? "Parent Section Name" : "Section Name"}
                    </label>
                    <input
                      value={activeSection.name}
                      onChange={(event) => updateSection(activeSection.id, { name: event.target.value })}
                      className="w-full max-w-md p-3 rounded-xl border bg-white text-gray-900"
                    />
                  </div>
                  {activeSection.parent_section_id ? (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase mb-2">Section Color</p>
                      <p className="text-sm text-gray-500">
                        Uses {sections.find((section) => section.id === activeSection.parent_section_id)?.name || "parent"} color
                      </p>
                      <span
                        className="mt-2 inline-block h-6 w-10 rounded border border-gray-200"
                        style={{
                          backgroundColor:
                            normalizeHex(
                              sections.find((section) => section.id === activeSection.parent_section_id)?.color
                            ) || "#e5e7eb",
                        }}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
                        Section Color
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={normalizeHex(activeSection.color) || "#059669"}
                          onChange={(event) => updateSection(activeSection.id, { color: event.target.value })}
                          className="h-10 w-14 rounded border border-gray-200 bg-white p-1"
                        />
                        <input
                          type="text"
                          value={activeSection.color || ""}
                          onChange={(event) => updateSection(activeSection.id, { color: event.target.value })}
                          placeholder="#059669"
                          className="w-32 p-2 rounded-lg border bg-white text-sm text-gray-900 uppercase"
                        />
                      </div>
                    </div>
                  )}
                  {activeHasChildren ? (
                    <p className="text-sm text-gray-500">
                      This section has subsections. Build the seat grid on a subsection instead.
                    </p>
                  ) : (
                    <div className="min-w-0 w-full">
                      <SeatLayoutBuilder
                        rowCount={activeSection.row_count || ""}
                        columnCount={activeSection.column_count || ""}
                        startRowLabel={activeSection.start_row_label ?? DEFAULT_START_ROW_LABEL}
                        startColumnNumber={
                          activeSection.start_column_number ?? DEFAULT_START_COLUMN_NUMBER
                        }
                        numberDirection={activeSection.number_direction || "ltr"}
                        seats={activeSection.seats || []}
                        onRowCountChange={(value) => updateSection(activeSection.id, { row_count: value })}
                        onColumnCountChange={(value) => updateSection(activeSection.id, { column_count: value })}
                        onStartRowLabelChange={(value) =>
                          updateSection(activeSection.id, { start_row_label: value })
                        }
                        onStartColumnNumberChange={(value) =>
                          updateSection(activeSection.id, { start_column_number: value })
                        }
                        onNumberDirectionChange={(value) =>
                          updateSection(activeSection.id, { number_direction: value === "rtl" ? "rtl" : "ltr" })
                        }
                        onSeatsChange={(nextSeats) => applySeatsChange(activeSection.id, nextSeats)}
                        onGenerate={handleGenerateGrid}
                      />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">Add a section to start building a seat layout.</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="bg-brand-maroon text-white px-10 py-3 rounded-xl font-black uppercase hover:bg-red-700 disabled:opacity-60 w-full sm:w-auto"
              >
                {saving ? "Saving..." : "Save Venue"}
              </button>
            </div>
          </form>

          {showSaveConfirm ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="save-venue-title"
                className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6"
              >
                <h3 id="save-venue-title" className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                  {isNew ? "Create Venue?" : "Save Changes?"}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {isNew ? (
                    <>
                      Are you sure you want to create{" "}
                      <span className="font-bold text-gray-800">{venue.name.trim() || "this venue"}</span>?
                    </>
                  ) : (
                    <>
                      Are you sure you want to save these changes to{" "}
                      <span className="font-bold text-gray-800">{venue.name.trim() || "this venue"}</span>?
                    </>
                  )}
                </p>
                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowSaveConfirm(false)}
                    disabled={saving}
                    className="px-4 py-2 min-h-11 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 min-h-11 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                  >
                    {saving ? "Saving..." : isNew ? "Create Venue" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {pendingDeleteIds ? (
            <div
              className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
              onClick={() => setPendingDeleteIds(null)}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-sections-title"
                className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6"
                onClick={(event) => event.stopPropagation()}
              >
                <h3 id="delete-sections-title" className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                  Delete {pendingDeleteRows.length} Section{pendingDeleteRows.length === 1 ? "" : "s"}?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {pendingDeleteRows.length === 1 ? "This section" : "These sections"} and {pendingDeleteRows.length === 1 ? "its" : "their"} seats will be removed from the venue template. Save the venue to keep this change.
                </p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mb-6">
                  <ul className="space-y-1">
                    {pendingDeleteRows.map((row) => (
                      <li key={row.id} className="text-xs font-semibold text-gray-800">
                        {row.label}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-wrap gap-3 justify-end">
                  <button
                    type="button"
                    autoFocus
                    onClick={() => setPendingDeleteIds(null)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyBulkDelete}
                    className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700"
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VenueEditor;
